/**
 * Mock Course Data for Academy
 * Initial 15 courses curated for MVP
 */

import type { Course } from '../types/academy';

export const MOCK_COURSES: Course[] = [
  // BEGINNERS CATEGORY (5 courses)
  {
    id: 'btc-101',
    title: 'Bitcoin 101: What is Cryptocurrency?',
    description: 'Learn the fundamentals of Bitcoin and cryptocurrency. Perfect starting point for beginners to understand blockchain technology and digital assets.',
    thumbnail: 'https://images.unsplash.com/photo-1518546305927-5a555bb7020d?w=1280&h=720&fit=crop',
    videoUrl: 'https://www.youtube.com/watch?v=41JCpzvnn_0', // Whiteboard Crypto
    instructor: 'Whiteboard Crypto',
    duration: 12,
    level: 'beginner',
    category: ['fundamentals'],
    tags: ['bitcoin', 'cryptocurrency', 'blockchain', 'basics'],
    type: 'video',
    isPro: false,
    isFeatured: true,
    rating: 4.9,
    ratingCount: 2450,
    views: 125000,
    releaseDate: new Date('2024-01-15'),
    whatYouLearn: [
      'What is Bitcoin and how it works',
      'Understanding blockchain technology',
      'The difference between Bitcoin and traditional money',
      'How to get started with crypto'
    ]
  },
  {
    id: 'how-to-buy',
    title: 'How to Buy Your First Bitcoin',
    description: 'Step-by-step guide to purchasing your first cryptocurrency safely. Learn about exchanges, wallets, and security best practices.',
    thumbnail: 'https://images.unsplash.com/photo-1621416894569-0f39ed31d247?w=1280&h=720&fit=crop',
    videoUrl: 'https://www.youtube.com/watch?v=EBJyHBKLSow', // Coin Bureau
    instructor: 'Coin Bureau',
    duration: 18,
    level: 'beginner',
    category: ['fundamentals'],
    tags: ['buying', 'exchanges', 'getting-started', 'security'],
    type: 'video',
    isPro: false,
    isFeatured: false,
    rating: 4.8,
    ratingCount: 1890,
    views: 89000,
    releaseDate: new Date('2024-02-01'),
    whatYouLearn: [
      'How to choose a cryptocurrency exchange',
      'Step-by-step buying process',
      'Setting up 2FA and security',
      'Transferring to your wallet'
    ]
  },
  {
    id: 'wallets-explained',
    title: 'Crypto Wallets Explained: Hot vs Cold',
    description: 'Understand the different types of cryptocurrency wallets and how to keep your assets secure. Learn the difference between hot and cold storage.',
    thumbnail: 'https://images.unsplash.com/photo-1563986768609-322da13575f3?w=1280&h=720&fit=crop',
    videoUrl: 'https://www.youtube.com/watch?v=d8IBpfs9bf4', // 99Bitcoins
    instructor: '99Bitcoins',
    duration: 15,
    level: 'beginner',
    category: ['fundamentals'],
    tags: ['wallets', 'security', 'hardware-wallet', 'storage'],
    type: 'video',
    isPro: false,
    isFeatured: false,
    rating: 4.7,
    ratingCount: 1560,
    views: 67000,
    releaseDate: new Date('2024-02-10'),
    whatYouLearn: [
      'Types of crypto wallets',
      'Hot vs cold storage',
      'How to use hardware wallets',
      'Seed phrase security'
    ]
  },
  {
    id: 'blockchain-basics',
    title: 'Blockchain Technology Explained Simply',
    description: 'Demystify blockchain technology with simple explanations and visual examples. No technical background required.',
    thumbnail: 'https://images.unsplash.com/photo-1639762681485-074b7f938ba0?w=1280&h=720&fit=crop',
    videoUrl: 'https://www.youtube.com/watch?v=SSo_EIwHSd4', // Simply Explained
    instructor: 'Simply Explained',
    duration: 10,
    level: 'beginner',
    category: ['fundamentals'],
    tags: ['blockchain', 'technology', 'education', 'basics'],
    type: 'video',
    isPro: false,
    isFeatured: false,
    rating: 4.9,
    ratingCount: 3200,
    views: 156000,
    releaseDate: new Date('2024-01-20'),
    whatYouLearn: [
      'What is a blockchain',
      'How blocks are created',
      'Understanding mining',
      'Real-world applications'
    ]
  },
  {
    id: 'wealthhub-setup',
    title: 'Edouard.ai Tutorial: Setup Your First Portfolio',
    description: 'Complete guide to setting up and managing your crypto portfolio in Edouard.ai. Learn all features and best practices.',
    thumbnail: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=1280&h=720&fit=crop',
    videoUrl: '', // To be created
    instructor: 'Edouard.ai Team',
    instructorAvatar: '🏦',
    duration: 20,
    level: 'beginner',
    category: ['fundamentals'],
    tags: ['wealthhub', 'tutorial', 'portfolio', 'getting-started'],
    type: 'video',
    isPro: false,
    isFeatured: true,
    rating: 5.0,
    ratingCount: 450,
    views: 12000,
    releaseDate: new Date('2024-11-01'),
    whatYouLearn: [
      'Creating your first portfolio',
      'Adding positions and transactions',
      'Understanding FIFO calculations',
      'Reading your dashboard metrics'
    ]
  },

  // PORTFOLIO MANAGEMENT CATEGORY (5 courses)
  {
    id: 'diversification-101',
    title: 'Portfolio Diversification Strategy',
    description: 'Learn how to build a diversified crypto portfolio that balances risk and reward. Professional strategies for long-term success.',
    thumbnail: 'https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=1280&h=720&fit=crop',
    videoUrl: 'https://www.youtube.com/watch?v=QoqfuuGKo40', // Benjamin Cowen
    instructor: 'Benjamin Cowen',
    duration: 22,
    level: 'intermediate',
    category: ['portfolio-management'],
    tags: ['diversification', 'risk', 'strategy', 'allocation'],
    type: 'video',
    isPro: false,
    isFeatured: false,
    rating: 4.8,
    ratingCount: 890,
    views: 45000,
    releaseDate: new Date('2024-03-01'),
    whatYouLearn: [
      'Portfolio allocation strategies',
      'Risk management principles',
      'Correlation between assets',
      'Rebalancing techniques'
    ]
  },
  {
    id: 'risk-management',
    title: 'Crypto Risk Management Essentials',
    description: 'Master risk management to protect your capital. Learn position sizing, stop losses, and the psychology of risk.',
    thumbnail: 'https://images.unsplash.com/photo-1590283603385-17ffb3a7f29f?w=1280&h=720&fit=crop',
    videoUrl: 'https://www.youtube.com/watch?v=C_hShSV3T1I', // Crypto Casey
    instructor: 'Crypto Casey',
    duration: 25,
    level: 'intermediate',
    category: ['portfolio-management'],
    tags: ['risk', 'management', 'position-sizing', 'stop-loss'],
    type: 'video',
    isPro: true,
    isFeatured: false,
    rating: 4.9,
    ratingCount: 1120,
    views: 56000,
    releaseDate: new Date('2024-03-15'),
    whatYouLearn: [
      'The 2% rule for position sizing',
      'Setting effective stop losses',
      'Risk/reward ratios',
      'Emotional control strategies'
    ]
  },
  {
    id: 'fifo-lifo',
    title: 'Understanding FIFO & Cost Basis Calculations',
    description: 'Deep dive into FIFO, LIFO, and other cost basis methods. Essential for tax reporting and portfolio tracking.',
    thumbnail: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=1280&h=720&fit=crop',
    videoUrl: '', // To be created
    instructor: 'Edouard.ai Team',
    instructorAvatar: '🏦',
    duration: 15,
    level: 'intermediate',
    category: ['portfolio-management'],
    tags: ['fifo', 'taxes', 'accounting', 'cost-basis'],
    type: 'video',
    isPro: false,
    isFeatured: false,
    rating: 4.7,
    ratingCount: 340,
    views: 8900,
    releaseDate: new Date('2024-11-05'),
    whatYouLearn: [
      'What is FIFO and how it works',
      'LIFO and other methods',
      'Tax implications',
      'Edouard.ai FIFO calculator'
    ]
  },
  {
    id: 'rebalancing-guide',
    title: 'Portfolio Rebalancing: When & How',
    description: 'Learn when and how to rebalance your portfolio for optimal performance. Includes automated strategies.',
    thumbnail: 'https://images.unsplash.com/photo-1642790106117-e829e14a795f?w=1280&h=720&fit=crop',
    videoUrl: 'https://www.youtube.com/watch?v=l_Rce3TW2_U', // InvestAnswers
    instructor: 'InvestAnswers',
    duration: 28,
    level: 'intermediate',
    category: ['portfolio-management'],
    tags: ['rebalancing', 'strategy', 'automation', 'portfolio'],
    type: 'video',
    isPro: true,
    isFeatured: false,
    rating: 4.9,
    ratingCount: 780,
    views: 42000,
    releaseDate: new Date('2024-04-01'),
    whatYouLearn: [
      'Threshold-based rebalancing',
      'Time-based rebalancing',
      'Tax-efficient strategies',
      'Automated rebalancing tools'
    ]
  },
  {
    id: 'dashboard-3d',
    title: 'Edouard.ai 3D Universe: Visualize Your Portfolio',
    description: 'Explore your portfolio in 3D space. Learn how to use the revolutionary 3D Universe view to analyze your holdings.',
    thumbnail: 'https://images.unsplash.com/photo-1462331940025-496dfbfc7564?w=1280&h=720&fit=crop',
    videoUrl: '', // To be created
    instructor: 'Edouard.ai Team',
    instructorAvatar: '🏦',
    duration: 12,
    level: 'beginner',
    category: ['portfolio-management'],
    tags: ['wealthhub', '3d', 'visualization', 'tutorial'],
    type: 'video',
    isPro: false,
    isFeatured: true,
    rating: 5.0,
    ratingCount: 520,
    views: 15000,
    releaseDate: new Date('2024-11-06'),
    whatYouLearn: [
      'Navigating the 3D Universe',
      'Understanding visual mappings',
      'Identifying portfolio risks',
      'Using interactive features'
    ]
  },

  // TRADING BASICS CATEGORY (5 courses)
  {
    id: 'reading-charts',
    title: 'Reading Your First Crypto Chart',
    description: 'Learn to read cryptocurrency charts like a pro. Understand candlesticks, timeframes, and basic patterns.',
    thumbnail: 'https://images.unsplash.com/photo-1642790595397-7047dc98fa72?w=1280&h=720&fit=crop',
    videoUrl: 'https://www.youtube.com/watch?v=oS88UxLQAkw', // Crypto Crew University
    instructor: 'Crypto Crew University',
    duration: 18,
    level: 'beginner',
    category: ['technical-analysis'],
    tags: ['charts', 'candlesticks', 'technical-analysis', 'trading'],
    type: 'video',
    isPro: false,
    isFeatured: false,
    rating: 4.8,
    ratingCount: 1450,
    views: 78000,
    releaseDate: new Date('2024-02-20'),
    whatYouLearn: [
      'Reading candlestick charts',
      'Understanding timeframes',
      'Basic chart patterns',
      'Volume analysis'
    ]
  },
  {
    id: 'order-types',
    title: 'Market vs Limit Orders Explained',
    description: 'Master different order types to execute trades efficiently. Learn when to use market, limit, and stop orders.',
    thumbnail: 'https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=1280&h=720&fit=crop',
    videoUrl: 'https://www.youtube.com/watch?v=NhVKVRFSQ6s', // Coin Bureau
    instructor: 'Coin Bureau',
    duration: 14,
    level: 'beginner',
    category: ['fundamentals'],
    tags: ['orders', 'trading', 'market', 'limit'],
    type: 'video',
    isPro: false,
    isFeatured: false,
    rating: 4.7,
    ratingCount: 890,
    views: 52000,
    releaseDate: new Date('2024-03-05'),
    whatYouLearn: [
      'Market orders vs limit orders',
      'Stop-loss orders',
      'Take-profit orders',
      'Advanced order types'
    ]
  },
  {
    id: 'trading-mistakes',
    title: 'Top 10 Crypto Trading Mistakes to Avoid',
    description: 'Learn from others\' mistakes. Common pitfalls that cost traders money and how to avoid them.',
    thumbnail: 'https://images.unsplash.com/photo-1621761191319-c6fb62004040?w=1280&h=720&fit=crop',
    videoUrl: 'https://www.youtube.com/watch?v=AJ5kqYvJYkE', // DataDash
    instructor: 'DataDash',
    duration: 16,
    level: 'beginner',
    category: ['fundamentals'],
    tags: ['mistakes', 'lessons', 'trading', 'psychology'],
    type: 'video',
    isPro: false,
    isFeatured: false,
    rating: 4.9,
    ratingCount: 2100,
    views: 134000,
    releaseDate: new Date('2024-03-20'),
    whatYouLearn: [
      'Common beginner mistakes',
      'Emotional trading pitfalls',
      'Risk management errors',
      'How to stay disciplined'
    ]
  },
  {
    id: 'trading-psychology',
    title: 'The Psychology of Trading Success',
    description: 'Master your emotions and develop winning trader mindset. Psychology is 80% of trading success.',
    thumbnail: 'https://images.unsplash.com/photo-1559526324-593bc073d938?w=1280&h=720&fit=crop',
    videoUrl: 'https://www.youtube.com/watch?v=lp8rGzh-VnQ', // Benjamin Cowen
    instructor: 'Benjamin Cowen',
    duration: 24,
    level: 'intermediate',
    category: ['portfolio-management'],
    tags: ['psychology', 'emotions', 'discipline', 'mindset'],
    type: 'video',
    isPro: true,
    isFeatured: false,
    rating: 4.9,
    ratingCount: 1680,
    views: 92000,
    releaseDate: new Date('2024-04-10'),
    whatYouLearn: [
      'Controlling fear and greed',
      'Developing discipline',
      'Creating trading rules',
      'Handling losses mentally'
    ]
  },
  {
    id: 'trading-bot-guide',
    title: 'Edouard.ai Trading Bot: Complete Guide',
    description: 'Learn to use the Edouard.ai AI Trading Bot for automated recommendations. Setup, configuration, and best practices.',
    thumbnail: 'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=1280&h=720&fit=crop',
    videoUrl: '', // To be created
    instructor: 'Edouard.ai Team',
    instructorAvatar: '🏦',
    duration: 30,
    level: 'intermediate',
    category: ['advanced-trading'],
    tags: ['wealthhub', 'bot', 'automation', 'ai'],
    type: 'video',
    isPro: true,
    isFeatured: true,
    rating: 4.8,
    ratingCount: 290,
    views: 7800,
    releaseDate: new Date('2024-11-07'),
    whatYouLearn: [
      'Bot profiles and configuration',
      'Understanding trading signals',
      'Sentiment analysis features',
      'Risk management settings'
    ]
  }
];

// Helper functions
export function getCourseById(id: string): Course | undefined {
  return MOCK_COURSES.find(course => course.id === id);
}

export function getCoursesByCategory(category: string): Course[] {
  return MOCK_COURSES.filter(course => course.category.includes(category as any));
}

export function getCoursesByLevel(level: string): Course[] {
  return MOCK_COURSES.filter(course => course.level === level);
}

export function getFeaturedCourses(): Course[] {
  return MOCK_COURSES.filter(course => course.isFeatured);
}

export function getFreeCourses(): Course[] {
  return MOCK_COURSES.filter(course => !course.isPro);
}

export function getProCourses(): Course[] {
  return MOCK_COURSES.filter(course => course.isPro);
}

export function getTrendingCourses(): Course[] {
  // Sort by views (descending)
  return [...MOCK_COURSES].sort((a, b) => b.views - a.views).slice(0, 10);
}

export function getRecentCourses(): Course[] {
  // Sort by release date (descending)
  return [...MOCK_COURSES].sort((a, b) => b.releaseDate.getTime() - a.releaseDate.getTime()).slice(0, 10);
}

export function searchCourses(query: string): Course[] {
  const lowerQuery = query.toLowerCase();
  return MOCK_COURSES.filter(course =>
    course.title.toLowerCase().includes(lowerQuery) ||
    course.description.toLowerCase().includes(lowerQuery) ||
    course.tags.some(tag => tag.toLowerCase().includes(lowerQuery)) ||
    course.instructor.toLowerCase().includes(lowerQuery)
  );
}
